import { useEffect, useState } from 'react'

import { api } from '../api/client'
import type { CustomFieldDefinition } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, ErrorBanner } from './common'

/**
 * La scheda dei campi personalizzati di un CI o di un Asset: mostra e modifica i
 * valori dei campi che il tenant ha definito e che valgono per questa scheda.
 *
 * Le definizioni vivono altrove (le governa un operatore in «Tassonomia»); qui
 * si compilano i valori. Se per questa scheda non c'è alcun campo definito, il
 * riquadro non compare: un riquadro vuoto prometterebbe un dato che non esiste.
 */
export function SchedaCampiPersonalizzati({
  entity,
  scope,
  values,
  onSalva,
  soloLettura = false,
}: {
  entity: 'ci' | 'asset'
  /** La classe (CI) o categoria (Asset) della scheda: decide quali campi valgono. */
  scope: string | null
  values: Record<string, unknown>
  /** Salva l'insieme completo dei valori. Risolve quando è andato a buon fine. */
  onSalva: (valori: Record<string, unknown>) => Promise<void>
  soloLettura?: boolean
}) {
  const defs = useAsync(() => api.customFields(entity), [entity])
  const applicabili = (defs.data ?? []).filter((d) => d.scope === null || d.scope === scope)

  const [bozza, setBozza] = useState<Record<string, unknown>>(values)
  const [salvando, setSalvando] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [salvato, setSalvato] = useState(false)

  // Riallinea la bozza quando arrivano le definizioni o cambiano i valori dal
  // server (dopo un salvataggio e ricarica): la scheda mostra sempre il vero.
  // Il confronto è sul contenuto, non sull'identità: il genitore può ricreare
  // l'oggetto `values` a ogni render, e dipendere dalla sua identità azzererebbe
  // ciò che si sta scrivendo a ogni battuta.
  const chiaveValori = JSON.stringify(values)
  useEffect(() => {
    setBozza(values)
    setSalvato(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defs.data, chiaveValori])

  if (defs.loading && !defs.data) return null
  if (applicabili.length === 0) return null

  const imposta = (key: string, valore: unknown) => {
    setBozza((prec) => ({ ...prec, [key]: valore }))
    setSalvato(false)
  }

  async function salva() {
    setSalvando(true)
    setErrore(null)
    try {
      // Solo i campi applicabili: un valore rimasto per un campo non più valido
      // (classe cambiata) non deve ripartire verso il server.
      const puliti: Record<string, unknown> = {}
      for (const d of applicabili) {
        const v = bozza[d.key]
        if (v !== undefined && v !== null && v !== '') puliti[d.key] = v
      }
      await onSalva(puliti)
      setSalvato(true)
    } catch (err) {
      setErrore(err)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Card title={t('Campi personalizzati')}>
      <ErrorBanner error={defs.error ?? errore} />
      <div className="campocustom__lista">
        {applicabili.map((d) => (
          <CampoInput
            key={d.id}
            def={d}
            value={bozza[d.key]}
            disabled={soloLettura || salvando}
            onChange={(v) => imposta(d.key, v)}
          />
        ))}
      </div>
      {!soloLettura && (
        <div className="campocustom__azioni">
          <button onClick={salva} disabled={salvando}>
            {salvando ? t('Salvo…') : t('Salva')}
          </button>
          {salvato && <span className="campocustom__ok">{t('Salvato')}</span>}
        </div>
      )}
    </Card>
  )
}

function CampoInput({
  def,
  value,
  disabled,
  onChange,
}: {
  def: CustomFieldDefinition
  value: unknown
  disabled: boolean
  onChange: (v: unknown) => void
}) {
  const id = `cf-${def.id}`
  return (
    <div className="field campocustom__campo">
      <label htmlFor={id}>
        {def.label}
        {def.required && <span className="campocustom__obbligo" title={t('Obbligatorio')}> *</span>}
      </label>
      <Widget id={id} def={def} value={value} disabled={disabled} onChange={onChange} />
      {def.help_text && <p className="field__hint">{def.help_text}</p>}
    </div>
  )
}

function Widget({
  id,
  def,
  value,
  disabled,
  onChange,
}: {
  id: string
  def: CustomFieldDefinition
  value: unknown
  disabled: boolean
  onChange: (v: unknown) => void
}) {
  if (def.field_type === 'boolean') {
    return (
      <label className="campocustom__switch">
        <input
          id={id}
          type="checkbox"
          checked={value === true || value === 'true'}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{value === true || value === 'true' ? t('Sì') : t('No')}</span>
      </label>
    )
  }
  if (def.field_type === 'select') {
    return (
      <select id={id} value={String(value ?? '')} disabled={disabled}
              onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {def.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    )
  }
  const tipo = def.field_type === 'number' ? 'number' : def.field_type === 'date' ? 'date' : 'text'
  return (
    <input
      id={id}
      type={tipo}
      value={value === null || value === undefined ? '' : String(value)}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}
